<template>
  <div class="form-group">
    <div class="autocomplete">
      <label
        :for="id"
        class="sr-only"
      >{{ $t('common:suggestion.fullTextSearch') }}</label>
      <input
        :id="id"
        v-model="keyword"
        type="text"
        class="form-control first"
        :placeholder="$t('common:suggestion.placeholder')"
        :autocomplete="autofill"
        @input="onChange"
        @keydown.down="onArrowDown"
        @keydown.up="onArrowUp"
        @keydown.enter="onEnter"
        @focus="onFocus"
      >
      <!-- do not change showResetButton to v-if => hydration error... -->
      <button
        v-show="showResetButton"
        :for="id"
        class="form-control-reset"
        type="button"
        :aria-label="$t('common:ariaLabel.suggestionResetButton')"
        @click.prevent="resetSuggestion"
      >
        <i class="icon"><icon-times /></i>
      </button>

      <ul
        v-show="isOpen"
        id="autocomplete-results"
        class="autocomplete-results"
      >
        <template
          v-for="suggests in results"
        >
          <li
            v-if="suggests.suggestionType != 99"
            :key="'typ_' + suggests.suggestionType"
            class="inactive"
          >
            {{ $t('common:suggestion.locationHeader.' + suggests.suggestionType) }}
          </li>
          <li
            v-for="suggest in suggests.suggestions"
            :key="suggest.id"
            v-dompurify-html="((suggests.suggestionType === 99) ?
              $t('common:suggestion.searchFor', { name: suggest.nameShow}) :
              suggest.nameShow)"
            class="autocomplete-result"
            :data-type="$t('common:suggestion.locationHeader.' + suggests.suggestionType)"
            :class="[{ 'is-active': suggest.id === arrowCounterID }]"
            tabindex="0"
            @click="setResult(suggest)"
            @keypress="setResult(suggest)"
          />
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import searchRedirect from 'mixins/composition/search/SearchFunctions';
import DeviceHelper from 'mixins/DeviceHelper';
import debounce from 'lodash.debounce';

/* ICONS */
import IconTimes from 'ICON:IconTimes';
import loadSuggestion from 'requests/suggestionRequest';

export default {
  name: 'Suggestion',
  i18nOptions: { namespaces: 'common' },
  components: {
    IconTimes,
  },
  mixins: [
    DeviceHelper,
  ],
  props: {
    onlyFillStore: {
      type: Boolean,
      default: false,
    },
    // used to set autocomplete value
    autofill: {
      type: String,
      default: 'off',
    },
    buttonShow: {
      type: Boolean,
      default: false,
    },
    linkNotToSearch: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    return {
      searchRedirect,
    };
  },
  data() {
    return {
      isOpen: false,
      results: [],
      resultsOrdered: [],
      searchResult: {},
      arrowCounter: -1,
      arrowCounterID: -1,
      country: '',
      countryName: '',
      region: '',
      place: '',
      offer: '',
      filename: '',
      suggestionSelected: false,
      scrollIntoViewDelay: false,
    };
  },
  computed: {
    keyword: {
      get() {
        return this.$store.getters.getSearchFilter('q') || this.$store.getters.getSearchFilter('fulltext') || '';
      },
      set(newKeyword) {
        this.setSearchFilter({
          q: newKeyword,
          fulltext: '',
        });
        this.$emit('set-keyword', newKeyword);
      },
    },
    // used to show the clear button
    showResetButton() {
      return (this.buttonShow && this.keyword !== '');
    },
    isMobile() {
      return ['xs', 'sm'].includes(this.dh_breakpoint);
    },
  },
  watch: {
    searchResult() {
      this.country = '';
      if (this.searchResult.country) this.country = this.searchResult.country;
      this.region = '';
      this.place = '';
      this.offer = '';
      if (this.searchResult.locationType === '1') {
        this.suggestionSelected = true;
      }
      if (this.searchResult.locationType === '2') {
        this.region = this.searchResult.locationNumber;
        this.countryName = this.searchResult.countryName;
        this.suggestionSelected = true;
      }
      if (this.searchResult.locationType === '3') {
        this.place = this.searchResult.locationNumber;
        this.countryName = this.searchResult.countryName;
        this.suggestionSelected = true;
      }
      if (this.searchResult.locationType === '4') {
        this.offer = this.searchResult.locationNumber;
        this.suggestionSelected = true;
      }
      if (this.searchResult.locationType === '98') {
        this.filename = this.searchResult.filename;
        this.suggestionSelected = true;
      }
      this.useSuggestion();
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    debounce(() => { this.addSubmitListener(); }, 500);
  },
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
    this.scrollIntoViewDelay = false; // reset timeout object
  },
  methods: {
    onChange(event) {
      this.country = '';
      this.region = '';
      this.place = '';
      this.offer = '';
      this.filename = '';
      this.suggestionSelected = false;
      this.arrowCounterID = -1;
      this.arrowCounter = -1;
      this.filterResults(event.target.value);
      this.isOpen = true;
    },
    filterResults(keyword) {
      if (keyword.length > 1) {
        loadSuggestion(keyword, this.$store.state.app.currentLanguage)
          .then((suggestions) => {
            this.resultsOrdered = [];
            if (suggestions) {
              this.results = suggestions;
              Object.entries(suggestions).forEach((locations) => {
                Object.entries(locations[1].suggestions).forEach((location) => {
                  this.resultsOrdered.push(location[1]);
                });
              });
            }
          })
          .catch(() => {
            // error handling, currently the suggestion simply returns nothing
          });
      } else {
        this.results = [];
        this.resultsOrdered = [];
      }
    },
    setResult(result) {
      this.keyword = result.name;
      this.searchResult = result;
      this.isOpen = false;
    },
    onArrowDown() {
      if (this.resultsOrdered.length && this.arrowCounter < this.resultsOrdered.length) {
        this.arrowCounter += 1;
        if (this.arrowCounter > this.resultsOrdered.length - 1) this.arrowCounter = this.resultsOrdered.length - 1;
        this.arrowCounterID = this.resultsOrdered[this.arrowCounter].id;
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter -= 1;
        if (this.arrowCounter < 0) this.arrowCounter = 0;
        this.arrowCounterID = this.resultsOrdered[this.arrowCounter].id;
      }
    },
    onEnter(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.resultsOrdered.length > 0) {
          this.checkSuggestionValue();
        }
      }
      // only use result if it is set
      if (this.arrowCounter === -1) {
        this.useSuggestion();
      }
      this.isOpen = false;
      this.arrowCounter = -1;
    },
    onFocus() {
      if (this.isMobile) {
        // TECL-2454 workaround to prevent out of viewport scrolled input field
        // only happens if modal was not ssr rendered
        this.scrollIntoViewDelay = setTimeout(() => {
          this.$el.scrollIntoView(true);
        }, 600);
      }
    },
    handleClickOutside(evt) {
      if (!this.$el.contains(evt.target)) {
        if (this.isOpen === true) {
          this.checkSuggestionValue();
          // only use result if it is set
          if (this.arrowCounter === -1) {
            this.useSuggestion();
          }
        }

        this.isOpen = false;
        this.arrowCounter = -1;
      }
      return false;
    },
    checkSuggestionValue() {
      if (this.suggestionSelected === false) {
        // get first result if nothing is selected
        if (this.arrowCounter === -1 && this.resultsOrdered[1]) {
          // check for perfect match
          if (this.resultsOrdered[1].name.trim().toLowerCase() === this.keyword.trim().toLowerCase()) {
            this.arrowCounter = 1;
          } else if (this.resultsOrdered[0].locationType === '98' && this.resultsOrdered.length === 2) {
            // if the only result is an object use it
            this.arrowCounter = 1;
          }
        }

        // only use result if it is set
        if (this.resultsOrdered[this.arrowCounter]) {
          this.setResult(this.resultsOrdered[this.arrowCounter]);
        }
      }
    },
    // include function from vuex mutations which can be used at several times
    ...mapMutations({ setSearchFilter: 'SET_SEARCHFILTER' }),
    resetSuggestion() {
      this.keyword = '';
      this.region = '';
      this.countryName = '';
      this.place = '';
      this.country = '';
      this.offer = '';
      this.filename = '';
      this.isOpen = false;
      // use nextTick here, otherwise keywords setter / getter won't be processed
      this.$nextTick(() => {
        this.onFocus();
        this.useSuggestion();
      });
    },
    useSuggestion() {
      if (this.filename !== '') {
        this.$emit('stop-submit', true);
        const searchObject = {
          q: '',
          t: '',
          region: '',
          place: '',
          country: '',
          offer: '',
          fulltext: '',
          BBoxString: '',
        };
        this.setSearchFilter(searchObject);
        this.$router.push(this.filename).catch(() => {});
      } else if (this.linkNotToSearch === true) { // HIER WEITER PROP ANLEGEN + AKTION
        // neue aktionen fpr requedt
        // this.setSearchFilter({
        //   region: this.region || '',
        //   place: this.place || '',
        //   country: this.country || '',
        //   BBoxString: '',
        // });
      } else if (this.onlyFillStore === true) {
        const searchObject = {
          q: ((this.suggestionSelected === true) ? this.keyword : ''),
          t: this.countryName || '',
          region: this.region || '',
          place: this.place || '',
          country: this.country || '',
          offer: this.offer || this.$store.getters.getSearchFilter('offer') || '',
          fulltext: ((this.suggestionSelected === false) ? this.keyword : ''),
          BBoxString: '',
        };
        this.setSearchFilter(searchObject);
      } else {
        searchRedirect({
          router: this.$router,
          filters: {
            q: ((this.suggestionSelected === true) ? this.keyword : ''),
            t: this.countryName || '',
            region: this.region || '',
            place: this.place || '',
            country: this.country || '',
            offer: this.offer || this.$store.getters.getSearchFilter('offer') || '',
            fulltext: ((this.suggestionSelected === false) ? this.keyword : ''),
            BBoxString: '',
            latitude: '',
            longitude: '',
            distance: '',
          },
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .autocomplete {
    position: relative;
  }

  .autocomplete-results {
    padding: 0;
    margin: 4px 0 0;
    border: 1px solid rba(#000, 0.5);

    @include box_shadow_small;

    background-color: #fff;
    overflow: auto;
    width: 100%;
    position: absolute;
    z-index: 100000;

    @include radius(0.25em);

    .inactive,
    .autocomplete-result {
      text-align: left;
      list-style: none;
      padding: 0.25em 0.5em;
      margin: 0;
      display: block;

      @include font_size_s;

      @media #{$media_xs} {
        @include font_size_m;
      }
    }

    .inactive {
      background: $color_grey_bg;
    }

    .autocomplete-result {
      cursor: pointer;

      &.is-active,
      &:hover {
        background-color: $color_ci_complement;
        color: #fff;
      }
    }
  }

  /deep/ mark {
    padding-left: 0;
    padding-right: 0;
  }
</style>
